import { Injectable } from "@angular/core";
import { GettingStartedSteps } from "../enums/getting-started-steps.enum";
import { AboutYouSteps } from "../enums/about-you-steps.enum";
import { IncomeAndAssetsSteps } from "../enums/income-and-assets-steps.enum";
import { PropertySteps } from "../enums/property-steps.enum";
import { SubmitSteps } from "../enums/submit-steps.enum";
import { StepperContextService } from "./stepper-context.service";
import { PreApprovalSteps } from "../enums/pre-approval-steps.enum";

@Injectable({ providedIn: 'root' })
export class StepperProgressBarService {
  constructor(
    private contextService: StepperContextService
  ) { }
  private stepStructure: Record<string, string[]> = {
    'getting-started': [GettingStartedSteps.LoanPurpose,
    GettingStartedSteps.HomebuyingStatus, GettingStartedSteps.RefinanceGoals],
    'about-you': [AboutYouSteps.BorrowerPersonalInfo, AboutYouSteps.BorrowerCitizenship,
    AboutYouSteps.BorrowerMilitary, AboutYouSteps.BorrowerCurrentAddress,
    AboutYouSteps.BorrowerOwnership],
    'property': [PropertySteps.OwnedAddressPlan, PropertySteps.RefinanceOwnedAddress,
    PropertySteps.PreviousOwnership, PropertySteps.PurchaseSubjectAddress,
    PropertySteps.RefinanceSubjectAddress, PropertySteps.RefinanceType,
    PropertySteps.RefinanceFirstLien, PropertySteps.RefinanceSecondLien,
    PropertySteps.PropertyUsage],
    'income-and-assets': [IncomeAndAssetsSteps.InvestmentRentalIncome,
    IncomeAndAssetsSteps.InvestmentExtraIncome,
    IncomeAndAssetsSteps.IncomeSource,
    IncomeAndAssetsSteps.OtherIncomeSource,
    IncomeAndAssetsSteps.OtherBankStatements,
    IncomeAndAssetsSteps.OtherSupportIncome
    ],
    'submit': [SubmitSteps.Confirmation, SubmitSteps.CreateLoanSuccess]
  };

  private stepStructurePreApproval: Record<string, string[]> = {
    'about-you': [AboutYouSteps.BorrowerCurrentAddress,
    AboutYouSteps.BorrowerCitizenship, AboutYouSteps.BorrowerMilitary],
    'property': [PropertySteps.PropertyUsage],
    'pre-approval': [PreApprovalSteps.StatedIncome,
    PreApprovalSteps.PurchasePrice,
    PreApprovalSteps.DownPayment,
    PreApprovalSteps.CreditCheck,
    PreApprovalSteps.Error
    ]
  };

  calculateProgress(currentStep: string): number {
    let progress = 0;
    const structure = this.stepStructure
    const sectionKeys = Object.keys(structure);
    const sectionCount = sectionKeys.length;
    const sectionWeight = 100 / sectionCount;

    for (const section of sectionKeys) {
      const steps = structure[section];
      const stepIndex = steps.indexOf(currentStep);

      if (stepIndex === -1) {
        // Add full section weight for previous sections
        progress += sectionWeight;
      } else {
        const stepWeight = sectionWeight / steps.length;
        progress += stepWeight * (stepIndex + 1); // +1 because it's 1-based progression
        break;
      }
    }

    return Math.min(progress, 100);
  }

  calculateProgressPreApproval(currentStep: string): number {
    let progress = 0;
    const structure = this.stepStructurePreApproval;

    const sectionWeights: Record<string, number> = {
      'about-you': 10,
      'property': 10,
      'pre-approval': 80
    };

    for (const section of Object.keys(structure)) {
      const steps = structure[section];
      const stepIndex = steps.indexOf(currentStep);

      if (stepIndex === -1) {
        // Add full weight for completed sections
        progress += sectionWeights[section];
      } else {
        // Distribute section weight across steps in that section
        const stepWeight = sectionWeights[section] / steps.length;
        progress += stepWeight * (stepIndex + 1); // +1 for 1-based progress
        break;
      }
    }

    return Math.min(progress, 100);
  }
}